import { noop } from 'lodash';
import React, { createContext, memo, ReactNode, useContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import EmailValidationModal from '../components/EmailValidationModal';
import { emitNotification } from '../components/Notification';
import { AUTH0_LOGOUT_URL } from '../config';
import { fetchFromRestAPI, useAuthFetchFromRestAPI } from './api';
import { IdToken, useAuth0, User } from "@auth0/auth0-react";
import { fetchUserAccountRemote } from "./UserAccount";

type AuthResult = {
  accessToken: string,
  idToken: string,
  expiresAt: number,
}

type ExtendedAuthContext = {
  isAdminUser: () => Promise<boolean>,
  handleOnAuthenticated: (accessToken: string, user: User) => void,
  handleOnSignedIn: () => void,
  isApprovedTherapist: () => Promise<boolean>,
}

const ExtendedAuthContext = createContext<ExtendedAuthContext>({
  isAdminUser: () => Promise.resolve(false),
  handleOnAuthenticated: noop,
  handleOnSignedIn: noop,
  isApprovedTherapist: () => Promise.resolve(false),
});

export const useExtendedAuth = () => {
  const context = useContext(ExtendedAuthContext);
  if (!context) {
    throw new Error('useExtendedAuth must be used within ExtendedAuthProvider');
  }
  return context;
}

// export const webAuth = new WebAuth({
//    clientID: AUTH0_CLIENT_ID,
//    domain: AUTH0_DOMAIN ? AUTH0_DOMAIN : process.env.VITE_AUTH0_DOMAIN,
//    redirectUri: AUTH0_LOGOUT_URL,
//  });

const Auth0ProviderMemo = ({ children }: { children: ReactNode } ) => {

  const auth0 = useAuth0();
  const { authFetch } = useAuthFetchFromRestAPI();

  const [requestedVerificationEMail, setRequestedVerificationEMail] = useState(false);
  const [lastAuthToken, setLastAuthToken] = useState<string | null>(null);
  const [emailValidationModalShow, setEmailValidationModalShow] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies<string>([]);

  // Not used since no distinction between signon and login possible
  const handleOnSignedIn = () => {
    emitNotification(
      <div>
        <big>
          Wir haben dir soeben eine E-Mail geschickt. Bitte bestätige deine E-Mail Adresse, damit du enlivio nutzen
          kannst.
        </big>
        <br />
        <br />
        Solltest du Fragen haben, schicke uns eine E-mail an{' '}
        <a href="mailto:info@enlivio.com">info@enlivio.com</a>
      </div>,
      {
        position: 'top-center',
        autoClose: 20000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      }
    );
  };

  const resendVerificationMail = async (token: string | null): Promise<void> => {
    if (!token) {
      return;
    }
    const response = await fetchFromRestAPI('/api/v1/user-profile/verification-email', {
      method: 'POST',
      token: token,
    });
    if (response.ok) {
      setRequestedVerificationEMail(true);
    }
  };

  const handleOnAuthenticated = (accessToken: string, user: User) => {
    if (user && !user.email_verified && user.sub && user.sub.startsWith('auth0|')) {
      setLastAuthToken(accessToken);
      setEmailValidationModalShow(true);
      clearSessionLocalStorage();
      return;
    }
  };

  const clearSessionLocalStorage = () => {
    cleanAuthCookies();
  };

  const cleanAuthCookies = () => {
    if (!cookies) {
      return;
    }
    // We need to clean auth0 cookies for old sessions and in case of using not main domain like staging
    const cookiesKeys = Object.keys(cookies);
    cookiesKeys.forEach((cookie) => {
      if (cookie.includes('auth0') || cookie.includes('did')) {
        setCookie(cookie, '', { expires: new Date() });
        removeCookie(cookie);
      }
    });
  };

  const extendedLogout = async () => {
    clearSessionLocalStorage();

    await auth0.logout({ logoutParams: { returnTo: AUTH0_LOGOUT_URL } });
  };

  // Replace by Role Based Access Control if wanted
  const isAdminUserBasedOnEmail = async (): Promise<boolean> => {
    const idToken: IdToken | undefined = await auth0.getIdTokenClaims();
    return !!idToken && !!idToken.email && (idToken.email.endsWith('enlivio.com') || idToken.email.endsWith('fraiss.com'));
  }

  const isApprovedTherapist = async (): Promise<boolean> => {
    const userProfileResponse = await fetchUserAccountRemote(authFetch);
    return !!userProfileResponse && userProfileResponse.approved;
  }

  // useEffect(() => {
  //   const accessTokenExpires = localStorage.getItem('expires_at');
  //   const accessToken = localStorage.getItem('access_token');
  //   if (accessTokenExpires && accessToken) {
  //     // access token in locale storage
  //     const now = Date.now();
  //     if (parseInt(accessTokenExpires) > parseInt(now.toString())) {
        // if (logOut) {
        //   setLogOut(false);
        //   return;
        // }
        // const idToken = localStorage.getItem('id_token');
        // const accessToken = localStorage.getItem('access_token');
        // const user = auth0.user;
        // setIdToken({ __raw: idToken });
        // setAccessToken({ __raw: accessToken });
        // setIsAuthenticated(true);
        // setIsAdminUser(user.email.endsWith('enlivio.com') || user.email.endsWith('fraiss.com'));
        // setLoading(false);
      // } else {
  //       console.log('Access token expired, clearing storage');
  //       localStorage.removeItem('access_token');
  //       localStorage.removeItem('id_token');
  //       localStorage.removeItem('expires_at');
  //       localStorage.removeItem('_online_');
  //     }
  //   }
  // }, [logOut]);

  return (
    <ExtendedAuthContext.Provider
      value={{
        isAdminUser: isAdminUserBasedOnEmail,
        isApprovedTherapist,
        handleOnSignedIn: handleOnSignedIn,
        handleOnAuthenticated: handleOnAuthenticated,
      }}
    >
      {children}
      <EmailValidationModal
        show={emailValidationModalShow}
        sent={requestedVerificationEMail}
        onPositive={() => {
          resendVerificationMail(lastAuthToken);
          setTimeout(() => {
            setEmailValidationModalShow(false);
            setLastAuthToken(null);
            setRequestedVerificationEMail(false);
            extendedLogout();
          }, 1000);
        }}
        onNegative={ async () => {
          setEmailValidationModalShow(false);
          setLastAuthToken(null);
          setRequestedVerificationEMail(false);
          await extendedLogout();
        }}
      />
    </ExtendedAuthContext.Provider>
  );
};

export const ExtendedAuthProvider = memo(Auth0ProviderMemo);
